import { Container, Title, Stack, TextInput, Group, CloseButton, Box } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useEffect, useState, useMemo } from "react";
import { getPatientsPaginated } from "../../utils/api-communication/RestApiCli";
import { displayPatientName, formatDateOnly, parseDateRangeOnly } from "../../utils/CommonUtil";
import { useTranslation } from "react-i18next";
import { navigate } from "../../utils/navigationService";
import TestList from "../result_list/TestList";
import { ColumnDefinition, TestListItem } from "../result_list/types/testListTypes";

export default function NewPatientsList() {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [name, setName] = useState("");
  const [uuid, setUuid] = useState("");

  useEffect(() => {
    setLoading(true);
    const req = {
      page: page,
      limit: 10,
      name: name,
      patientId: uuid,
    };
    if (dateRange[0]) {
      req["startDate"] = dateRange[0].toISOString();
    }
    if (dateRange[1]) {
      req["endDate"] = dateRange[1].toISOString();
    }
    getPatientsPaginated(
      req,
      (response) => {
        const data = response.data;
        console.log("new batch lenght", data.data.length);
        setPatients(data.data);
        setTotalPages(data.lastPage);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      },
    );
  }, [page, name, dateRange, uuid]);

  const patientColumns: ColumnDefinition[] = useMemo(
    () => [
      {
        dataField: "patientName",
        text: t("NewTest.PatientName"),
        formatter: (cell) => displayPatientName(cell),
      },
      {
        dataField: "dob",
        text: t("NewTest.Dob"),
        formatter: (cell) => formatDateOnly(cell),
      },
      {
        dataField: "uuid",
        text: "ID",
      },
    ],
    [t],
  );

  const mappedPatients: TestListItem[] = useMemo(() => {
    return patients.map((patient) => ({
      uuid: patient.uuid,
      patientName: patient.name,
      name: patient.name,
      dob: patient.dob,
      startDate: patient.dob,
      testType: "",
      eye: "",
    }));
  }, [patients]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemClick = (item: TestListItem) => {
    navigate(`/patient-page?uuid=${item.uuid}`);
  };

  return (
    <Container>
      <Title my={"md"} size="h2">
        {t("PatientList.title")}
      </Title>
      <Stack>
        <Group gap="md" grow w={"100%"}>
          <TextInput
            rightSection={name.length > 0 && <CloseButton c="gray.4" onClick={() => setName("")} />}
            label={t("OpaReports.name")}
            placeholder={t("OpaReports.name")}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <DatePickerInput
            clearable
            type="range"
            label={t("OpaReports.dob")}
            placeholder={t("OpaReports.dob")}
            value={dateRange}
            onChange={(v) => setDateRange(parseDateRangeOnly(v))}
          />
          <TextInput
            rightSection={uuid.length > 0 && <CloseButton c="gray.4" onClick={() => setUuid("")} />}
            label={t("OpaReports.patientId")}
            placeholder={t("OpaReports.patientId")}
            value={uuid}
            onChange={(event) => setUuid(event.currentTarget.value)}
          />
        </Group>
        <Box w="100%">
          <TestList
            data={mappedPatients}
            columns={patientColumns}
            loading={loading}
            showDateGroups={false}
            totalPages={totalPages}
            currentPage={page}
            onPageChange={handlePageChange}
            onItemClick={handleItemClick}
          />
        </Box>
      </Stack>
    </Container>
  );
}
